import {
	useDeleteVetterEntityMutation,
	useDeleteVetterUserMutation,
	useInviteVetterUserMutation,
	useGetVetterEntityQuery,
	useUpdateManufacturerMutation,
	useUpdateVetterEntityMutation,
} from '@codezo/graphql';
import { formatDate, formatMonthYear, timeAgo } from '../../lib/utils';
import { vetterInvitationStatus } from '../../lib/status-configs';
import { toast } from 'sonner';
import {
	ArrowLeft,
	ChevronRight,
	Factory,
	MoreHorizontal,
	Pencil,
	Plus,
	Shield,
	Star,
	Send,
	StarOff,
	Trash2,
	Users,
} from 'lucide-react';
import { useState } from 'react';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { useNavigate, useParams } from 'react-router-dom';
import { EditVetterModal } from './EditVetterModal';
import { InviteMemberModal } from './InviteMemberModal';
import { EditMemberModal, type MemberData } from './EditMemberModal';
import { AssignManufacturerModal } from './AssignManufacturerModal';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SectionCard } from '../../components/ui/section-card';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { EmptyState } from '../../components/ui/empty-state';
import { DetailRow } from '../../components/ui/detail-row';
import { InitialsAvatar } from '../../components/ui/initials-avatar';
import { StatusBadge } from '../../components/ui/status-badge';

/* ── Loading skeleton ── */

function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<div className="mb-5 h-4 w-16 animate-pulse rounded bg-surface-fill" />
			<div className="mb-7 flex items-start gap-4">
				<div className="h-[72px] w-[72px] animate-pulse rounded-[var(--r-3)] bg-surface-fill" />
				<div className="flex-1">
					<div className="mb-2 h-7 w-48 animate-pulse rounded bg-surface-fill" />
					<div className="h-4 w-72 animate-pulse rounded bg-surface-fill" />
				</div>
			</div>
			<div className="mb-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={i}
						className="rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-5 py-4"
					>
						<div className="mb-2 h-3 w-16 animate-pulse rounded bg-surface-fill" />
						<div className="h-6 w-10 animate-pulse rounded bg-surface-fill" />
					</div>
				))}
			</div>
		</div>
	);
}

/* ════════════════════════ VETTER DETAIL PAGE ════════════════════════ */

export function VetterDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [inviteMemberOpen, setInviteMemberOpen] = useState(false);
	const [assignOpen, setAssignOpen] = useState(false);
	const [editMemberTarget, setEditMemberTarget] = useState<MemberData | null>(null);
	const [deleteUserTarget, setDeleteUserTarget] = useState<{ id: string; name: string } | null>(
		null
	);
	const [unassignTarget, setUnassignTarget] = useState<{ id: string; name: string } | null>(null);

	const { data, loading, refetch } = useGetVetterEntityQuery({
		variables: { id: id! },
		fetchPolicy: 'no-cache',
		skip: !id,
	});

	const vetter = data?.getVetterEntity;
	useSetPageMeta(vetter?.name || 'Vetter', [
		{ label: 'Vetters', path: '/vetters' },
		{ label: vetter?.name || 'Loading...' },
	]);

	const [deleteVetterEntity, { loading: deleting }] = useDeleteVetterEntityMutation();
	const handleDelete = async () => {
		if (!vetter) return;
		try {
			await deleteVetterEntity({ variables: { id: vetter.id } });
			toast.success('Vetter deleted');
			navigate('/vetters');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete vetter');
		}
	};

	/**
	 * `invitedAt` tells us whether an email has gone out, so the action can say
	 * send vs re-send — but VetterUser still carries no status, so we cannot tell
	 * whether they have *activated*. The action therefore stays enabled and the
	 * backend's rejection surfaces as the error toast.
	 */
	const [inviteVetterUser, { loading: invitingUser }] = useInviteVetterUserMutation();
	const [inviteUserTarget, setInviteUserTarget] = useState<{
		id: string;
		name: string;
		invitedAt?: string | null;
	} | null>(null);
	const handleInviteUser = async () => {
		const target = inviteUserTarget;
		if (!target) return;
		try {
			const { data } = await inviteVetterUser({ variables: { id: target.id } });
			if (data?.inviteVetterUser?.success) {
				toast.success(
					target.invitedAt
						? `Invitation re-sent to ${target.name}`
						: `Invitation sent to ${target.name}`
				);
				// invitedAt drives the member's sub-line and menu label.
				refetch();
			} else {
				toast.error(data?.inviteVetterUser?.message || 'Failed to send invitation');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to send invitation');
		}
		setInviteUserTarget(null);
	};

	const [deleteVetterUser, { loading: deletingUser }] = useDeleteVetterUserMutation();
	const handleDeleteUser = async () => {
		if (!deleteUserTarget) return;
		try {
			await deleteVetterUser({ variables: { id: deleteUserTarget.id } });
			toast.success('Team member removed');
			setDeleteUserTarget(null);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove team member');
		}
	};

	const [updateVetterEntity, { loading: settingPrimary }] = useUpdateVetterEntityMutation();
	// The backend only accepts a member of this same entity, so this is always
	// driven from a row in the team-member list below.
	const handleSetPrimaryContact = async (vetterUserId: string | null) => {
		if (!vetter) return;
		try {
			const { data: res } = await updateVetterEntity({
				variables: {
					id: vetter.id,
					input: { primaryContactVetterUserId: vetterUserId },
				},
			});
			if (res?.updateVetterEntity?.success) {
				toast.success(vetterUserId ? 'Primary contact updated' : 'Primary contact cleared');
				refetch();
			} else {
				toast.error(res?.updateVetterEntity?.message || 'Failed to set primary contact');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to set primary contact');
		}
	};

	const [updateManufacturer, { loading: unassigning }] = useUpdateManufacturerMutation();
	const handleUnassign = async () => {
		if (!unassignTarget) return;
		try {
			const { data: res } = await updateManufacturer({
				variables: { input: { id: unassignTarget.id, vetterEntityId: null } },
			});
			if (res?.updateManufacturer?.success) {
				toast.success('Manufacturer unassigned');
				setUnassignTarget(null);
				refetch();
			} else {
				toast.error(res?.updateManufacturer?.message || 'Failed to unassign manufacturer');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to unassign manufacturer');
		}
	};

	if (loading) {
		return <DetailSkeleton />;
	}

	if (!vetter) {
		return (
			<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
				<button
					onClick={() => navigate('/vetters')}
					className="mb-4 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
				>
					<ArrowLeft size={14} />
					Vetters
				</button>
				<EmptyState
					icon={Shield}
					title="Vetter not found"
					description="This vetter may have been deleted or the link is invalid."
				>
					<Button
						variant="outline"
						size="sm"
						className="mt-1"
						onClick={() => navigate('/vetters')}
					>
						Back to vetters
					</Button>
				</EmptyState>
			</div>
		);
	}

	const users = vetter.vetterUsers ?? [];
	const manufacturers = vetter.manufacturers ?? [];
	const primaryContact = vetter.primaryContact;

	const deleteImpact = [
		users.length > 0 && `${users.length} team member${users.length === 1 ? '' : 's'}`,
		manufacturers.length > 0 &&
			`${manufacturers.length} assigned manufacturer${manufacturers.length === 1 ? '' : 's'}`,
	].filter(Boolean);
	const deleteDescription = `Are you sure you want to delete "${vetter.name}"?${
		deleteImpact.length ? ` This vetter has ${deleteImpact.join(' and ')}.` : ''
	} This action cannot be undone.`;

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			{/* ── Back ── */}
			<button
				onClick={() => navigate('/vetters')}
				className="mb-4 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				Vetters
			</button>

			{/* ── Header ── */}
			<div className="mb-7 flex items-start gap-4">
				<InitialsAvatar name={vetter.name} size={72} shape="rounded" />
				<div className="min-w-0 flex-1">
					<div className="mb-1 flex flex-wrap items-center gap-2.5">
						<h1 className="text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
							{vetter.name}
						</h1>
						<StatusBadge active={vetter.isActive} />
					</div>
					<div className="flex flex-wrap items-center gap-3.5 text-base text-text-3">
						{vetter.description && <span>{vetter.description}</span>}
						{vetter.createdAt && (
							<>
								{vetter.description && <span className="text-text-4">·</span>}
								<span>Since {formatMonthYear(vetter.createdAt)}</span>
							</>
						)}
					</div>
				</div>
				<div className="flex shrink-0 gap-2">
					<Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
						Edit
					</Button>
					<Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
						Delete
					</Button>
				</div>
			</div>

			{/* ── Metric strip ── */}
			<div className="mb-7 grid grid-cols-2 divide-x divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken sm:grid-cols-4">
				{[
					{ label: 'Status', value: vetter.isActive ? 'Active' : 'Inactive' },
					{ label: 'Team members', value: String(users.length) },
					{ label: 'Manufacturers', value: String(manufacturers.length) },
					{
						label: 'Created',
						value: vetter.createdAt ? formatDate(vetter.createdAt) : '—',
					},
				].map((metric) => (
					<div key={metric.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{metric.label}
						</div>
						<div className="font-mono text-lg font-medium text-text-1">
							{metric.value}
						</div>
					</div>
				))}
			</div>

			{/* ── Content ── */}
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
				{/* Left column */}
				<div className="flex flex-col gap-4">
					{/* Team members */}
					<SectionCard
						title={`Team members (${users.length})`}
						action={
							<button onClick={() => setInviteMemberOpen(true)}>
								<Plus size={12} />
								Invite member
							</button>
						}
					>
						{users.length === 0 ? (
							<div className="flex flex-col items-center gap-3 py-14 text-center">
								<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
									<Users size={20} />
								</div>
								<div className="text-base text-text-2">No team members</div>
								<div className="text-sm text-text-4">
									Invite users to this vetter entity.
								</div>
							</div>
						) : (
							<div className="overflow-x-auto">
								<Table className="min-w-[900px]">
									<TableHeader>
										<TableRow className="border-line-1 hover:bg-transparent">
											<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Member
											</TableHead>
											<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Phone
											</TableHead>
											<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Invitation
											</TableHead>
											{/*
											 * Last active, not an "Active" badge: VetterUser has
											 * no status, so a date we actually have beats a
											 * status we would be inferring.
											 */}
											<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Last active
											</TableHead>
											<TableHead className="w-10" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{users.map((u) => {
											const isPrimary =
												u.id === vetter.primaryContactVetterUserId;
											const invitation = vetterInvitationStatus(u.invitedAt);
											return (
												<TableRow
													key={u.id}
													className="border-line-1 last:border-b-0"
												>
													<TableCell>
														<div className="flex items-center gap-3">
															<InitialsAvatar
																name={`${u.firstName} ${u.lastName}`}
																size={32}
																shape="rounded"
															/>
															<div className="min-w-0">
																<div className="flex items-center gap-1.5">
																	<span className="truncate text-base font-medium text-text-1">
																		{u.firstName} {u.lastName}
																	</span>
																	{isPrimary && (
																		<span className="flex shrink-0 items-center gap-1 text-xs text-accent">
																			<Star size={11} />
																			Primary
																		</span>
																	)}
																</div>
																<div className="truncate text-sm text-text-3">
																	{u.authenticationUser?.email ||
																		'—'}
																</div>
															</div>
														</div>
													</TableCell>
													<TableCell className="text-base text-text-2">
														{u.phone || '—'}
													</TableCell>
													<TableCell>
														<StatusBadge
															active
															activeLabel={invitation.label}
															activeTone={invitation.tone}
														/>
													</TableCell>
													<TableCell className="text-base text-text-3">
														{u.authenticationUser?.lastActive
															? timeAgo(
																	u.authenticationUser.lastActive
																)
															: 'Never'}
													</TableCell>
													<TableCell>
														<div className="flex justify-end">
															<DropdownMenu>
																<DropdownMenuTrigger className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
																	<MoreHorizontal size={14} />
																</DropdownMenuTrigger>
																<DropdownMenuContent align="end">
																	<DropdownMenuItem
																		disabled={invitingUser}
																		onClick={() =>
																			setInviteUserTarget({
																				id: u.id,
																				name: `${u.firstName} ${u.lastName}`,
																				invitedAt:
																					u.invitedAt,
																			})
																		}
																	>
																		<Send size={14} />
																		{u.invitedAt
																			? 'Re-send invitation'
																			: 'Send invitation'}
																	</DropdownMenuItem>
																	<DropdownMenuItem
																		onClick={() =>
																			setEditMemberTarget({
																				id: u.id,
																				firstName:
																					u.firstName,
																				lastName:
																					u.lastName,
																				email: u
																					.authenticationUser
																					?.email,
																				phone: u.phone,
																				notes: u.notes,
																			})
																		}
																	>
																		<Pencil size={14} /> Edit
																	</DropdownMenuItem>
																	{isPrimary ? (
																		<DropdownMenuItem
																			disabled={
																				settingPrimary
																			}
																			onClick={() =>
																				handleSetPrimaryContact(
																					null
																				)
																			}
																		>
																			<StarOff size={14} />{' '}
																			Clear primary contact
																		</DropdownMenuItem>
																	) : (
																		<DropdownMenuItem
																			disabled={
																				settingPrimary
																			}
																			onClick={() =>
																				handleSetPrimaryContact(
																					u.id
																				)
																			}
																		>
																			<Star size={14} /> Set
																			as primary contact
																		</DropdownMenuItem>
																	)}
																	<DropdownMenuItem
																		variant="destructive"
																		onClick={() =>
																			setDeleteUserTarget({
																				id: u.id,
																				name: `${u.firstName} ${u.lastName}`,
																			})
																		}
																	>
																		<Trash2 size={14} /> Remove
																	</DropdownMenuItem>
																</DropdownMenuContent>
															</DropdownMenu>
														</div>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</div>
						)}
					</SectionCard>

					{/* Assigned manufacturers */}
					<SectionCard
						title={`Assigned manufacturers (${manufacturers.length})`}
						action={
							<button onClick={() => setAssignOpen(true)}>
								<Plus size={12} />
								Assign
							</button>
						}
					>
						{manufacturers.length === 0 ? (
							<div className="flex flex-col items-center gap-3 py-14 text-center">
								<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
									<Factory size={20} />
								</div>
								<div className="text-base text-text-2">No manufacturers</div>
								<div className="text-sm text-text-4">
									Assign manufacturers to this vetter.
								</div>
							</div>
						) : (
							<div>
								{manufacturers.map((mfr, i) => (
									<div
										key={mfr.id}
										className="flex items-center gap-3.5 px-4 py-3.5 transition-colors duration-[180ms] hover:bg-surface-hover"
										style={{
											borderBottom:
												i < manufacturers.length - 1
													? '0.5px solid var(--line-1)'
													: 'none',
										}}
									>
										<div
											className="flex min-w-0 flex-1 cursor-pointer items-center gap-3.5"
											onClick={() => navigate(`/suppliers/${mfr.id}`)}
										>
											<InitialsAvatar
												name={mfr.name}
												src={mfr.logoUrl}
												size={32}
												shape="rounded"
											/>
											<div className="min-w-0 flex-1">
												<div className="text-base font-medium text-text-1">
													{mfr.name}
												</div>
												<div className="truncate text-sm text-text-4">
													{[
														mfr.country,
														mfr.website
															?.replace(/^https?:\/\//, '')
															.replace(/\/$/, ''),
													]
														.filter(Boolean)
														.join(' · ') || 'No details'}
												</div>
											</div>
										</div>
										<StatusBadge active={mfr.isActive} />
										<DropdownMenu>
											<DropdownMenuTrigger className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
												<MoreHorizontal size={14} />
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem
													onClick={() => navigate(`/suppliers/${mfr.id}`)}
												>
													<ChevronRight size={14} /> View
												</DropdownMenuItem>
												<DropdownMenuItem
													variant="destructive"
													onClick={() =>
														setUnassignTarget({
															id: mfr.id,
															name: mfr.name,
														})
													}
												>
													<Trash2 size={14} /> Unassign
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								))}
							</div>
						)}
					</SectionCard>
				</div>

				{/* Right column */}
				<div className="flex flex-col gap-4">
					{/* Primary contact card */}
					<SectionCard title="Primary contact">
						{primaryContact ? (
							<>
								<div className="flex items-center gap-3 px-4 py-3.5">
									<InitialsAvatar
										name={`${primaryContact.firstName} ${primaryContact.lastName}`}
										size={36}
										shape="rounded"
									/>
									<div className="min-w-0 flex-1 text-base font-medium text-text-1">
										{primaryContact.firstName} {primaryContact.lastName}
									</div>
								</div>
								<div style={{ borderTop: '0.5px solid var(--line-1)' }}>
									<DetailRow
										label="Email"
										value={
											primaryContact.authenticationUser?.email ? (
												<a
													href={`mailto:${primaryContact.authenticationUser.email}`}
													className="text-text-1 underline decoration-line-2 underline-offset-2 transition-colors hover:decoration-text-1"
												>
													{primaryContact.authenticationUser.email}
												</a>
											) : null
										}
									/>
									<DetailRow
										label="Phone"
										value={
											primaryContact.phone ? (
												<a
													href={`tel:${primaryContact.phone.replace(/\s+/g, '')}`}
													className="text-text-1 underline decoration-line-2 underline-offset-2 transition-colors hover:decoration-text-1"
												>
													{primaryContact.phone}
												</a>
											) : null
										}
										last
									/>
								</div>
							</>
						) : (
							<div className="px-4 py-4 text-sm text-text-4">
								{users.length === 0
									? 'No primary contact — invite a team member first.'
									: 'No primary contact set. Pick one from the team member list.'}
							</div>
						)}
					</SectionCard>

					{/* Details card */}
					<SectionCard title="Details">
						<DetailRow label="Team members" value={users.length} />
						<DetailRow label="Manufacturers" value={manufacturers.length} />
						<DetailRow
							label="Created"
							value={vetter.createdAt ? formatDate(vetter.createdAt) : null}
						/>
						<DetailRow
							label="Updated"
							value={vetter.updatedAt ? formatDate(vetter.updatedAt) : null}
							last
						/>
					</SectionCard>

					{/* Description */}
					{vetter.description && (
						<SectionCard title="Description">
							<div className="px-4 py-3 text-sm leading-relaxed text-text-2">
								{vetter.description}
							</div>
						</SectionCard>
					)}

					{/* Notes */}
					<SectionCard title="Notes">
						{vetter.notes ? (
							<div className="whitespace-pre-wrap px-4 py-3 text-sm leading-relaxed text-text-2">
								{vetter.notes}
							</div>
						) : (
							<div className="px-4 py-3 text-sm text-text-4">
								No notes yet. Use Edit to add one.
							</div>
						)}
					</SectionCard>
				</div>
			</div>

			{/*
			 * Re-sending emails the person again and leaves the earlier invitation
			 * link valid, so the copy says which of the two is about to happen.
			 */}
			<ConfirmDialog
				open={!!inviteUserTarget}
				onOpenChange={(open) => {
					if (!open) setInviteUserTarget(null);
				}}
				title={inviteUserTarget?.invitedAt ? 'Re-send invitation' : 'Send invitation'}
				description={
					inviteUserTarget?.invitedAt
						? `${inviteUserTarget.name} was already emailed an invitation ${timeAgo(inviteUserTarget.invitedAt)}. Sending another emails them again, and the earlier invitation link stays valid.`
						: `${inviteUserTarget?.name ?? ''} will be emailed an invitation to set up their account.`
				}
				confirmLabel={inviteUserTarget?.invitedAt ? 'Re-send' : 'Send invitation'}
				onConfirm={handleInviteUser}
				loading={invitingUser}
			/>

			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title="Delete vetter"
				description={deleteDescription}
				onConfirm={handleDelete}
				loading={deleting}
			/>

			<ConfirmDialog
				open={!!deleteUserTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteUserTarget(null);
				}}
				title="Remove team member"
				description={`Are you sure you want to remove "${deleteUserTarget?.name}" from this vetter entity?`}
				confirmLabel="Remove"
				onConfirm={handleDeleteUser}
				loading={deletingUser}
			/>

			<ConfirmDialog
				open={!!unassignTarget}
				onOpenChange={(open) => {
					if (!open) setUnassignTarget(null);
				}}
				title="Unassign manufacturer"
				description={`Remove "${unassignTarget?.name}" from ${vetter.name}? The manufacturer will no longer be linked to this vetter.`}
				confirmLabel="Unassign"
				onConfirm={handleUnassign}
				loading={unassigning}
			/>

			<InviteMemberModal
				open={inviteMemberOpen}
				onOpenChange={setInviteMemberOpen}
				vetterEntityId={vetter.id}
				onSuccess={() => refetch()}
			/>

			<EditMemberModal
				open={!!editMemberTarget}
				onOpenChange={(open) => {
					if (!open) setEditMemberTarget(null);
				}}
				member={editMemberTarget}
				onSuccess={() => refetch()}
			/>

			<AssignManufacturerModal
				open={assignOpen}
				onOpenChange={setAssignOpen}
				vetterEntityId={vetter.id}
				onSuccess={() => refetch()}
			/>

			<EditVetterModal
				open={editOpen}
				onOpenChange={setEditOpen}
				vetter={
					vetter
						? {
								id: vetter.id,
								name: vetter.name,
								description: vetter.description,
								notes: vetter.notes,
								isActive: vetter.isActive,
								primaryContactVetterUserId: vetter.primaryContactVetterUserId,
								members: users,
							}
						: null
				}
				onSuccess={() => refetch()}
			/>
		</div>
	);
}
